/**
 * FacebookComments Component (OWZ-32)
 *
 * Demonstrates `pages_manage_engagement` by listing recent comments across
 * the Page's posts and allowing reply / hide / delete from the admin.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ExternalLink,
  EyeOff,
  Eye,
  Loader2,
  MessageSquare,
  RefreshCw,
  Send,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { facebook } from '@/services/api/admin';

const AUTO_REFRESH_MS = 60_000;

const formatWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const FacebookComments = () => {
  const { toast } = useToast();
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyOpen, setReplyOpen] = useState({});
  const [replyText, setReplyText] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const refreshTimer = useRef(null);

  const fetchComments = useCallback(async () => {
    setError(null);
    try {
      const data = await facebook.comments.list({ days: 14, limit: 50 });
      setComments(data.comments || []);
    } catch (err) {
      setError(err.message || 'Failed to load comments');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') {
        fetchComments();
      }
    };
    refreshTimer.current = setInterval(tick, AUTO_REFRESH_MS);
    return () => clearInterval(refreshTimer.current);
  }, [fetchComments]);

  const handleReply = async (commentId) => {
    const message = (replyText[commentId] || '').trim();
    if (!message) return;
    setBusyId(commentId);
    try {
      await facebook.comments.reply(commentId, message);
      toast({ title: 'Reply sent' });
      setReplyOpen({ ...replyOpen, [commentId]: false });
      setReplyText({ ...replyText, [commentId]: '' });
      await fetchComments();
    } catch (err) {
      toast({
        title: 'Reply failed',
        description: err.message || 'Could not post reply',
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleHide = async (comment) => {
    setBusyId(comment.id);
    try {
      await facebook.comments.hide(comment.id, !comment.is_hidden);
      toast({ title: comment.is_hidden ? 'Comment unhidden' : 'Comment hidden' });
      await fetchComments();
    } catch (err) {
      toast({
        title: 'Action failed',
        description: err.message || 'Could not update comment',
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await facebook.comments.delete(deleteTarget.id);
      toast({ title: 'Comment deleted' });
      setDeleteTarget(null);
      await fetchComments();
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err.message || 'Could not delete comment',
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Recent Comments</CardTitle>
          <CardDescription>
            Comments from the last 14 days across your Page's posts.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={fetchComments} aria-label="Refresh">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : comments.length === 0 ? (
          <div className="py-12 text-center text-gray-500 dark:text-gray-400">
            <MessageSquare className="h-10 w-10 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p>No recent comments.</p>
          </div>
        ) : (
          <ul className="divide-y dark:divide-gray-700">
            {comments.map((comment) => (
              <li key={comment.id} className="py-3 space-y-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">
                        {comment.from?.name || 'Anonymous'}
                      </p>
                      {comment.is_hidden && (
                        <Badge variant="outline">Hidden</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatWhen(comment.created_time)}
                      {comment.post_message ? ` · on "${comment.post_message.slice(0, 60)}${comment.post_message.length > 60 ? '…' : ''}"` : ''}
                    </p>
                  </div>
                  {comment.post_permalink_url && (
                    <a
                      href={comment.post_permalink_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 dark:text-blue-400 inline-flex items-center gap-1"
                    >
                      View post <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                <p className="text-sm whitespace-pre-wrap">{comment.message}</p>

                <div className="flex items-center gap-2 text-sm">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setReplyOpen({ ...replyOpen, [comment.id]: !replyOpen[comment.id] })
                    }
                    disabled={busyId === comment.id}
                  >
                    <Send className="h-3 w-3 mr-1" /> Reply
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleHide(comment)}
                    disabled={busyId === comment.id}
                  >
                    {comment.is_hidden ? (
                      <><Eye className="h-3 w-3 mr-1" /> Unhide</>
                    ) : (
                      <><EyeOff className="h-3 w-3 mr-1" /> Hide</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeleteTarget(comment)}
                    disabled={busyId === comment.id}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                  </Button>
                </div>

                {replyOpen[comment.id] && (
                  <div className="space-y-2 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                    <Textarea
                      value={replyText[comment.id] || ''}
                      onChange={(e) =>
                        setReplyText({ ...replyText, [comment.id]: e.target.value })
                      }
                      placeholder="Write a public reply…"
                      rows={2}
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleReply(comment.id)}
                        disabled={busyId === comment.id || !(replyText[comment.id] || '').trim()}
                      >
                        {busyId === comment.id ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Send className="h-3 w-3 mr-1" />
                        )}
                        Send reply
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setReplyOpen({ ...replyOpen, [comment.id]: false })
                        }
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this comment?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the comment from your Facebook Page. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default FacebookComments;
